<template>
  <header class="app-header">
    <div class="header-container">
      <div class="logo-section">
        <img src="/rosterOwl1logo.png" alt="Roster OWL" class="logo" />
      </div>

      <!-- Mobile Hamburger Menu (centered) -->
      <button 
        v-if="authStore.isAuthenticated" 
        @click="toggleMobileMenu" 
        class="mobile-hamburger" 
        :class="{ active: showMobileMenu }"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <!-- Mobile User Name (right side) -->
      <div v-if="authStore.isAuthenticated" class="mobile-user-name">
        {{ authStore.user?.first_name }} {{ authStore.user?.last_name }}
      </div>
      
      <nav class="header-nav" v-if="authStore.isAuthenticated">
        <!-- System Admin Navigation -->
        <router-link v-if="authStore.hasAdminAccess" to="/saas/companies" class="nav-link">
          🏢 Companies
        </router-link>
        <router-link v-if="authStore.hasSchedulerAccess || authStore.hasAdminAccess" to="/saas/scheduling" class="nav-link">
          🤖 AI Scheduler
        </router-link>
        
        <!-- Regular Tenant Navigation -->
        <router-link v-if="authStore.user?.role === 'superuser' && !authStore.hasAdminAccess" to="/superadmin" class="nav-link">
          🦉 Superadmin
        </router-link>
        <router-link v-if="!authStore.hasAdminAccess" to="/dashboard" class="nav-link">
          🏠 {{ $t('navigation.dashboard') }}
        </router-link>
        <router-link v-if="authStore.user?.role === 'manager' && !authStore.hasAdminAccess" to="/shifts" class="nav-link">
          🗓️ {{ $t('navigation.shiftCalendar') }}
        </router-link>
        <router-link v-if="authStore.user?.role === 'manager' && !authStore.hasAdminAccess" to="/manager" class="nav-link">
          📊 {{ $t('timeReporting.timeReportApproval') }}
        </router-link>
        <router-link v-if="authStore.user?.role === 'manager' && !authStore.hasAdminAccess" to="/user-management" class="nav-link">
          👥 {{ $t('userManagement.title') }}
        </router-link>
      </nav>

      <div class="header-actions" v-if="authStore.isAuthenticated">
        <!-- Language Switcher -->
        <div class="language-switcher">
          <button 
            @click="switchLanguage('sv')" 
            class="lang-btn"
            :class="{ active: currentLocale === 'sv' }"
          >
            SV
          </button>
          <button 
            @click="switchLanguage('en')" 
            class="lang-btn"
            :class="{ active: currentLocale === 'en' }"
          >
            EN
          </button>
        </div>

        <div class="user-info">
          <span class="user-name">{{ authStore.user?.first_name }} {{ authStore.user?.last_name }}</span>
          <span class="user-role">{{ authStore.user?.role ? $t('roles.' + authStore.user.role) : '' }}</span>
        </div>
        
        <!-- Quick Actions Dropdown (replacing logout button position) -->
        <div class="quick-actions-dropdown">
          <button @click="toggleQuickActions" class="quick-actions-btn">
            ⚡ {{ $t('navigation.settings') }}
            <span class="dropdown-arrow" :class="{ 'open': showQuickActions }">▼</span>
          </button>
          <div v-if="showQuickActions" class="dropdown-menu">
            <button @click="goToProfile" class="dropdown-item">
              👤 {{ $t('navigation.profile') }}
            </button>
            <button @click="goToTimeReporting" class="dropdown-item">
              📊 {{ $t('navigation.timeReporting') }}
            </button>
            <button @click="viewMyShifts" class="dropdown-item">
              📅 {{ $t('shiftCalendar.title') }}
            </button>
            <button @click="requestVacation" class="dropdown-item">
              🏖️ {{ $t('common.import') }}
            </button>
            <button @click="handleLogout" class="dropdown-item logout-item">
              🚪 {{ $t('navigation.logout') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile Menu Dropdown -->
      <div v-if="showMobileMenu" class="mobile-menu-dropdown">
        <!-- Mobile Language Switcher -->
        <div class="mobile-language-switcher">
          <button 
            @click="switchLanguage('sv')" 
            class="mobile-lang-btn"
            :class="{ active: currentLocale === 'sv' }"
          >
            🇸🇪 Svenska
          </button>
          <button 
            @click="switchLanguage('en')" 
            class="mobile-lang-btn"
            :class="{ active: currentLocale === 'en' }"
          >
            🇬🇧 English
          </button>
        </div>

        <!-- System Admin Navigation -->
        <router-link v-if="authStore.hasAdminAccess" to="/saas/companies" class="mobile-nav-link" @click="closeMobileMenu">
          🏢 Companies
        </router-link>
        <router-link v-if="authStore.hasSchedulerAccess || authStore.hasAdminAccess" to="/saas/scheduling" class="mobile-nav-link" @click="closeMobileMenu">
          🤖 AI Scheduler
        </router-link>
        
        <!-- Regular Tenant Navigation -->
        <router-link v-if="authStore.user?.role === 'superuser' && !authStore.hasAdminAccess" to="/superadmin" class="mobile-nav-link" @click="closeMobileMenu">
          🦉 Superadmin
        </router-link>
        <router-link v-if="!authStore.hasAdminAccess" to="/dashboard" class="mobile-nav-link" @click="closeMobileMenu">
          🏠 {{ $t('navigation.dashboard') }}
        </router-link>
        <router-link v-if="!authStore.hasAdminAccess" to="/profile" class="mobile-nav-link" @click="closeMobileMenu">
          👤 {{ $t('navigation.profile') }}
        </router-link>
        <router-link v-if="!authStore.hasAdminAccess" to="/time-reporting" class="mobile-nav-link" @click="closeMobileMenu">
          📊 {{ $t('navigation.timeReporting') }}
        </router-link>
        <router-link v-if="authStore.user?.role === 'manager' && !authStore.hasAdminAccess" to="/shifts" class="mobile-nav-link" @click="closeMobileMenu">
          🗓️ {{ $t('navigation.shiftCalendar') }}
        </router-link>
        <router-link v-if="authStore.user?.role === 'manager' && !authStore.hasAdminAccess" to="/manager" class="mobile-nav-link" @click="closeMobileMenu">
           📊 {{ $t('timeReporting.timeReportApproval') }}
        </router-link>
        <router-link v-if="authStore.user?.role === 'manager' && !authStore.hasAdminAccess" to="/user-management" class="mobile-nav-link" @click="closeMobileMenu">
          👥 {{ $t('userManagement.title') }}
        </router-link>
        <button @click="handleLogout" class="mobile-nav-link logout-btn">
          🚪 {{ $t('navigation.logout') }}
        </button>
      </div>
    </div>
  </header>

  <!-- Custom Modal -->
  <CustomModal 
    ref="customModal"
    type="alert"
    :title="modalTitle"
    :message="modalMessage"
    confirm-text="OK"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import CustomModal from './CustomModal.vue'

const authStore = useAuthStore()
const router = useRouter()
const { locale } = useI18n()
const showQuickActions = ref(false)
const showMobileMenu = ref(false)
const customModal = ref(null)
const modalMessage = ref('')
const modalTitle = ref('')

const currentLocale = computed(() => locale.value)

function switchLanguage(lang) {
  locale.value = lang
  localStorage.setItem('language', lang)
}

function toggleMobileMenu() {
  showMobileMenu.value = !showMobileMenu.value
  showQuickActions.value = false // Close quick actions when opening mobile menu
}

function closeMobileMenu() {
  showMobileMenu.value = false
}

function handleLogout() {
  authStore.clearTokens()
  localStorage.removeItem('user')
  showQuickActions.value = false
  showMobileMenu.value = false
  router.push('/')  // Redirect to login page
}

function toggleQuickActions() {
  showQuickActions.value = !showQuickActions.value
  showMobileMenu.value = false // Close mobile menu when opening quick actions
}

// Quick action functions
function goToProfile() {
  router.push('/profile')
  showQuickActions.value = false
}

function goToTimeReporting() {
  router.push('/time-reporting')
  showQuickActions.value = false
}

function viewMyShifts() {
  modalTitle.value = 'Mina Skift'
  modalMessage.value = 'Denna funktionalitet kommer snart! Vi arbetar på att implementera en vy där du kan se alla dina tilldelade skift.'
  customModal.value.show()
  showQuickActions.value = false
}

function requestVacation() {
  modalTitle.value = 'Semesteransökan'
  modalMessage.value = 'Semesteransökan kommer snart! Vi utvecklar ett enkelt system för att begära semester och ledighet.'
  customModal.value.show()
  showQuickActions.value = false
}
</script>

<style scoped>
.app-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  overflow: visible;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo {
  width: 160px;
  height: 70px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.logo:hover {
  transform: scale(1.05);
}

.header-nav {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.nav-link {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  transform: translateY(-1px);
}

.nav-link.router-link-active {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.language-switcher {
  display: flex;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 0.25rem;
}

.lang-btn {
  padding: 0.4rem 0.75rem;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.lang-btn.active {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: white;
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.user-role {
  font-size: 0.75rem;
  opacity: 0.8;
  text-transform: capitalize;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  margin-top: 0.25rem;
}

/* Mobile Hamburger Menu */
.mobile-hamburger {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  transition: all 0.3s ease;
  z-index: 1001;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.mobile-hamburger:hover {
  background: rgba(255, 255, 255, 0.25);
}

.mobile-user-name {
  display: none;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.mobile-hamburger span {
  width: 100%;
  height: 3px;
  background: white;
  border-radius: 2px;
  transition: all 0.3s ease;
  display: block;
}

.mobile-hamburger.active span:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.mobile-hamburger.active span:nth-child(2) {
  opacity: 0;
}

.mobile-hamburger.active span:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

.mobile-menu-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  animation: slideDown 0.2s ease;
  z-index: 1001;
}

.mobile-language-switcher {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.mobile-lang-btn {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid rgba(102, 126, 234, 0.3);
  background: white;
  color: #667eea;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-lang-btn:hover {
  background: rgba(102, 126, 234, 0.1);
  border-color: #667eea;
}

.mobile-lang-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-nav-link {
  display: block;
  padding: 0.875rem 1rem;
  color: #0a2544;
  text-decoration: none;
  font-weight: 500;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-size: 1rem;
}

.mobile-nav-link:hover {
  background: rgba(33, 150, 243, 0.1);
  transform: translateX(4px);
}

.mobile-nav-link.router-link-active {
  background: rgba(33, 150, 243, 0.15);
  color: #2196f3;
  font-weight: 600;
}

.mobile-nav-link.logout-btn {
  color: #dc3545;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: 0.5rem;
  padding-top: 1rem;
  cursor: pointer;
}

.mobile-nav-link.logout-btn:hover {
  background: rgba(220, 53, 69, 0.1);
}

/* Tablet and small desktop - hide some elements to prevent overflow */
@media (max-width: 1210px) {
  .header-container {
    padding: 0 1rem;
    flex-wrap: nowrap;
  }
  
  .header-nav {
    gap: 0.75rem;
  }
  
  .nav-link {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }
  
  .user-info {
    display: none; /* Hide user info to save space */
  }
  
  .header-actions {
    gap: 0.5rem;
  }
}

/* Medium screens - further compression */
@media (max-width: 900px) {
  .header-container {
    padding: 0 0.75rem;
  }
  
  .logo {
    width: 130px;
    height: 60px;
  }
  
  .header-nav {
    gap: 0.5rem;
  }
  
  .nav-link {
    padding: 0.4rem 0.6rem;
    font-size: 0.85rem;
  }
  
  .lang-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }
  
  .quick-actions-btn {
    padding: 0.4rem 0.75rem;
    font-size: 0.9rem;
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .header-container {
    padding: 0 0.5rem;
    height: 60px;
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
  }
  
  .logo-section {
    justify-self: start;
  }

  .logo {
    width: 120px;
    height: 55px;
  }
  
  .mobile-hamburger {
    display: flex;
    position: relative;
    left: auto;
    transform: none;
    justify-self: center;
  }

  .mobile-user-name {
    display: block;
    justify-self: end;
    text-align: right;
  }
  
  .header-nav {
    display: none;
  }
  
  .header-actions {
    display: none;
  }

  .user-info {
    display: none;
  }

  .quick-actions-dropdown {
    display: none;
  }

  .language-switcher {
    display: none;
  }
}

/* Quick Actions Dropdown */
.quick-actions-dropdown {
  position: relative;
  display: flex;
  align-items: center;
}

.quick-actions-btn {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.quick-actions-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.dropdown-arrow {
  transition: transform 0.2s ease;
  font-size: 0.75rem;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item {
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  color: #333;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
}

.dropdown-item:hover {
  background: rgba(33, 150, 243, 0.1);
  color: #2196f3;
}

.dropdown-item:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.logout-item {
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  color: #d32f2f;
}

.logout-item:hover {
  background: rgba(211, 47, 47, 0.1);
  color: #d32f2f;
}

@media (max-width: 480px) {
  .logo {
    width: 100px;
    height: 50px;
  }

  .mobile-user-name {
    font-size: 0.8rem;
    max-width: 100px;
  }
  
  .quick-actions-dropdown {
    display: none;
  }
}
</style>